import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GlobalSearch from '../../components/GlobalSearch';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import NotificationBell from '../../components/NotificationBell';
import UserDropdownMenu from '../../components/UserDropdownMenu';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import SetupProgressBanner from '../../components/SetupProgressBanner';
import axios from 'axios';
import {
  LayoutDashboard, Package, UserCircle, LogOut, Menu, X, Activity, Zap, ShoppingBag, Users, CreditCard, BookOpen, MessageSquare, Receipt, BarChart2, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupplierLayout = () => {
  const { user, logout, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMsg, setUnreadMsg] = useState(0);

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Unread messages badge
  useEffect(() => {
    if (!token) return;
    const fetchUnread = () => {
      axios.get(`${API}/messages/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setUnreadMsg(r.data.unread || 0))
        .catch(() => {});
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30000);
    return () => clearInterval(t);
  }, [token]);

  const navItems = [
    { label: t('sidebar.dashboard'),  icon: LayoutDashboard, path: '/app/supplier/dashboard' },
    { label: t('sidebar.myProfile'),  icon: UserCircle,      path: '/app/supplier/profile' },
    { label: t('sidebar.myCatalogue'),icon: Package,         path: '/app/supplier/catalogue' },
    { label: t('sidebar.findBriefs'), icon: Search,          path: '/app/supplier/find-briefs' },
    { label: t('sidebar.analytics'),  icon: BarChart2,       path: '/app/supplier/analytics' },
    { label: t('sidebar.messages'),   icon: MessageSquare,   path: '/app/supplier/messages' },
    { label: t('sidebar.team'),       icon: Users,           path: '/app/supplier/team' },
    { label: 'Faturalar',             icon: Receipt,         path: '/app/supplier/invoices' },
    { label: t('sidebar.billing'),    icon: CreditCard,      path: '/app/supplier/billing' },
  ];

  return (
    <>
      <GlobalSearch />
      <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 flex flex-col fixed inset-y-0 left-0 lg:static w-64 bg-white shadow-lg z-30 transition-transform duration-300 ease-in-out`}>
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-500 flex items-center justify-center shadow">
                <ShoppingBag className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Supplier Portal</p>
                <p className="text-xs text-gray-400">Expo Stand</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const isMessages = item.path.includes('/messages');
            return (
              <NavLink key={item.path} to={item.path}
                onClick={() => { setSidebarOpen(false); if (isMessages) setUnreadMsg(0); }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'text-gray-600 hover:bg-gray-100'
                  }`
                }
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{item.label}</span>
                {isMessages && unreadMsg > 0 && (
                  <span className="bg-purple-600 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold px-1">
                    {unreadMsg}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-100">
          {/* Setup Progress Banner */}
          <SetupProgressBanner
            role="supplier"
            accentColor="purple"
            token={token}
            onboardingUrl="/app/supplier/onboarding"
            doneKey="supplier_onboarding_done"
            profileApi={null}
            totalSteps={5}
          />
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-400 flex items-center justify-center">
              <span className="text-white text-xs font-bold">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-800 truncate">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" size="sm" className="w-full text-gray-600 border-gray-200 hover:bg-gray-50">
            <LogOut className="w-3.5 h-3.5 mr-2" />{t('sidebar.logout')}
          </Button>
        </div>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded hover:bg-gray-100">
                <Menu className="w-5 h-5" />
              </button>
              <Badge className="bg-purple-100 text-purple-700 border-purple-200">Supplier</Badge>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <NotificationBell accentColor="purple" />
              <UserDropdownMenu dashboardRoute="/app/supplier/dashboard" />
            </div>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
    </>
  );
};

export default SupplierLayout;
